import { getFileUrl } from '@/clients/http'
import { Mail, Phone, User as UserIcon } from 'lucide-react'
import { Image as ImageIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { TextInput, SwitchInput } from '@/components/atoms'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { CustomerModel as CustomerType } from '@/types'

import { CustomerFormState, customerFieldsConfig } from './customerFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface CustomerDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editCustomer: CustomerType.Customer | null
  closeDialog: () => void
  onCreate: (data: CustomerFormState) => void
  onUpdate: (id: string, data: CustomerFormState) => void
  loading?: boolean
}

export const CustomerDialog = ({
  open,
  mode,
  editCustomer,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: CustomerDialogProps) => {
  const [galleryOpen, setGalleryOpen] = useState(false)
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<CustomerFormState>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      status: 'active',
      image_id: '',
      image_url_temp: '',
    },
  })

  const { fields } = customerFieldsConfig()
  const formValues = watch()
  const status = watch('status')

  useEffect(() => {
    if (open) {
      if ((mode === 'edit' || mode === 'view') && editCustomer) {
        reset({
          name: editCustomer.name,
          email: editCustomer.email || '',
          phone: editCustomer.phone || '',
          status: editCustomer.status === 'active' ? 'active' : 'inactive',
          image_id: editCustomer.image_id || '',
          image_url_temp: editCustomer.image?.path || '',
        })
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          status: 'active',
          image_id: '',
          image_url_temp: '',
        })
      }
    }
  }, [open, mode, editCustomer, reset])

  const onSubmit = async (data: CustomerFormState) => {
    // Ensure image is treated as ID
    const imageId = data.image_id

    // Clean up empty fields
    const payload = { ...data, image_id: imageId }
    delete payload.image_url_temp
    if (!payload.email) delete payload.email
    if (!payload.phone) delete payload.phone

    if (mode === 'create') {
      onCreate(payload)
    } else {
      if (!editCustomer?._id) return
      onUpdate(editCustomer._id, payload)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <>
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create'
                ? 'Add customer'
                : mode === 'edit'
                  ? `Edit ${editCustomer?.name ?? 'customer'}`
                  : 'Customer details'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'view'
                ? 'View customer details.'
                : 'Manage customer profile information.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-2">
            <div className="w-full max-w-60">
              <div className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-subdued p-4">
                {formValues.image_url_temp ? (
                  <div className="relative w-full aspect-square overflow-hidden rounded-full group">
                    <img
                      src={getFileUrl(formValues.image_url_temp as string)}
                      alt="Customer Profile"
                      className="size-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setGalleryOpen(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setValue('image_url_temp', '')
                          setValue('image_id', '')
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <span className="inline-grid size-12 place-items-center rounded-full bg-muted">
                      <ImageIcon className="size-5 text-muted-foreground" />
                    </span>
                    <p className="text-sm text-text-2">No image</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setGalleryOpen(true)}
                      disabled={mode === 'view'}
                    >
                      Select image
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 py-2">
            <TextInput
              control={control}
              name="name"
              label="Full name"
              placeholder="Customer name"
              disabled={mode === 'view'}
              rules={fields.name.rules}
              errors={errors}
              icon={<UserIcon className="size-4" />}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextInput
                control={control}
                name="email"
                label="Email"
                placeholder="customer@example.com"
                disabled={mode === 'view'}
                rules={fields.email.rules}
                errors={errors}
                icon={<Mail className="size-4" />}
              />

              <TextInput
                control={control}
                name="phone"
                label="Phone"
                placeholder="017XXXXXXXX"
                disabled={mode === 'view'}
                rules={fields.phone.rules}
                errors={errors}
                icon={<Phone className="size-4" />}
              />
            </div>

            <SwitchInput
              control={control}
              name="status"
              label="Account status"
              description={formValues.status === 'active'
                ? 'Customer account is active.'
                : 'Customer account is inactive.'}
              disabled={mode === 'view'}
              activeValue="active"
              inactiveValue="inactive"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeDialog} disabled={loading} className="w-full sm:w-auto">
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)} className="w-full sm:w-auto">
                {loading && <Spinner />}
                {mode === 'create' ? 'Add customer' : 'Save'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('image_url_temp', files[0].url)
            setValue('image_id', files[0].id)
          }
        }}
      />
    </>
  )
}
